import React, { useEffect, useState } from 'react';
import { AiOutlineHome, AiOutlineMail } from 'react-icons/ai';
import { BiPhoneCall, BiTimeFive } from 'react-icons/bi';
import { FaPaperPlane } from 'react-icons/fa';
import axios from 'axios';
import { base_url, config } from '../utils/axiosConfig';

const ORANGE = '#ff6701';
const DARK   = '#232f3e';
const FONT   = '"Rubik", "Poppins", sans-serif';

const Contact = () => {
  const [contact, setContact] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError]   = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await axios.get(`${base_url}master/getcontact`, config);
        if (Array.isArray(res.data) && res.data.length > 0) setContact(res.data[0]);
        setIsError(false);
      } catch { setIsError(true); }
      finally { setIsLoading(false); }
    };
    fetchContact();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setForm({ name: '', email: '', phone: '', message: '' });
    setTimeout(() => setSent(false), 4000);
  };

  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', fontFamily: FONT }}>
      <div style={{ width: 40, height: 40, border: '4px solid #f0f0f0', borderTop: `4px solid ${ORANGE}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (isError) return (
    <div style={{ textAlign: 'center', padding: '80px 20px', color: '#ef4444', fontFamily: FONT }}>
      <p>Failed to load contact information. Please try again later.</p>
    </div>
  );

  const infoItems = [
    { icon: <AiOutlineHome size={18} />, label: 'Address',  value: contact.addres || 'N/A',  href: null },
    { icon: <BiPhoneCall    size={18} />, label: 'Phone',    value: contact.number || 'N/A',  href: `tel:${contact.number}` },
    { icon: <AiOutlineMail  size={18} />, label: 'Email',    value: contact.email  || 'N/A',  href: `mailto:${contact.email}` },
    { icon: <BiTimeFive     size={18} />, label: 'Hours',    value: contact.time   || 'Mon–Sat: 9am – 6pm', href: null },
  ];

  return (
    <div style={{ fontFamily: FONT, backgroundColor: '#f5f5f7', minHeight: '100vh' }}>

      {/* ── Page header ── */}
      <section style={{ background: `linear-gradient(135deg, ${DARK} 0%, #3b4149 100%)`, padding: '52px 20px', textAlign: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: ORANGE, textTransform: 'uppercase', letterSpacing: 2 }}>
          Reach Out
        </span>
        <h1 style={{ fontSize: 36, fontWeight: 800, color: '#fff', margin: '10px 0 12px' }}>
          Contact <span style={{ color: ORANGE }}>Us</span>
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', maxWidth: 500, margin: '0 auto' }}>
          Have a question or need help? We're here 24/7 — drop us a message and we'll get back to you.
        </p>
      </section>

      {/* ── Map ── */}
      <div style={{ lineHeight: 0 }}>
        <iframe
          src={contact.mapUrl ||
            'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d497511.57801003095!2d79.87895685!3d13.047512350000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5265ea4f7d3361%3A0x6e61a70b6863d433!2sChennai%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1728978350849!5m2!1sen!2sin'}
          width="100%"
          height="400"
          style={{ border: 0, display: 'block' }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Store Location"
        />
      </div>

      {/* ── Cards grid ── */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 20px 60px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, alignItems: 'start' }}>

          {/* ── Message form ── */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>Send Us a Message</h3>
            <p style={s.cardSub}>Fill the form and we'll reply within 24 hours.</p>

            {sent && (
              <div style={s.successBox}>
                ✅ Message sent! We'll be in touch soon.
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { field: 'name',    type: 'text',  placeholder: 'Your Name' },
                { field: 'email',   type: 'email', placeholder: 'Email Address' },
                { field: 'phone',   type: 'tel',   placeholder: 'Phone Number' },
              ].map(({ field, type, placeholder }) => (
                <input
                  key={field}
                  type={type}
                  required
                  placeholder={placeholder}
                  value={form[field]}
                  onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                  style={s.input}
                />
              ))}
              <textarea
                required
                rows={4}
                placeholder="Your Message"
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                style={{ ...s.input, resize: 'vertical', minHeight: 100 }}
              />
              <button type="submit" style={s.submitBtn}>
                <FaPaperPlane size={14} style={{ marginRight: 8 }} />
                Send Message
              </button>
            </form>
          </div>

          {/* ── Contact info ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={s.card}>
              <h3 style={s.cardTitle}>Get in Touch</h3>
              <p style={s.cardSub}>Visit us, call us, or send an email — we're always available.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                {infoItems.map(({ icon, label, value, href }) => (
                  <div key={label} style={s.infoRow}>
                    <div style={s.infoIcon}>{icon}</div>
                    <div>
                      <p style={{ fontSize: 11, color: '#aaa', margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</p>
                      {href
                        ? <a href={href} style={{ fontSize: 13, color: DARK, fontWeight: 600, textDecoration: 'none' }}>{value}</a>
                        : <p style={{ fontSize: 13, color: '#333', fontWeight: 600, margin: 0 }}>{value}</p>
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick CTA */}
            <div style={{ ...s.card, background: `linear-gradient(135deg, ${DARK} 0%, #3b4149 100%)` }}>
              <h4 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>
                Need Immediate Help?
              </h4>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: '0 0 16px' }}>
                Call us directly and we'll resolve any issue instantly.
              </p>
              <a
                href={`tel:${contact.number || ''}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  backgroundColor: ORANGE, color: '#fff',
                  padding: '11px 22px', borderRadius: 10,
                  fontWeight: 700, fontSize: 13, textDecoration: 'none',
                  boxShadow: '0 4px 14px rgba(255,103,1,0.4)',
                }}
              >
                <BiPhoneCall size={16} /> {contact.number || 'Call Now'}
              </a>
            </div>
          </div>
        </div>
      </section>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const s = {
  card: {
    backgroundColor: '#fff', borderRadius: 16, padding: '28px 24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
  },
  cardTitle: { fontSize: 18, fontWeight: 700, color: '#1c1c1b', margin: '0 0 4px' },
  cardSub:   { fontSize: 13, color: '#aaa', margin: '0 0 18px' },
  input: {
    width: '100%', padding: '12px 14px',
    border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 14, fontFamily: 'inherit', color: '#333',
    outline: 'none', boxSizing: 'border-box', backgroundColor: '#fafafa',
    transition: 'border-color 0.2s',
  },
  submitBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '13px 20px', width: '100%',
    backgroundColor: ORANGE, color: '#fff',
    border: 'none', borderRadius: 10,
    fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
    cursor: 'pointer', boxShadow: '0 4px 14px rgba(255,103,1,0.35)',
    transition: 'opacity 0.2s',
  },
  successBox: {
    padding: '12px 16px', borderRadius: 10, marginBottom: 14,
    backgroundColor: '#d1fae5', color: '#065f46',
    fontSize: 13, fontWeight: 600,
  },
  infoRow: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '10px 14px', borderRadius: 10, backgroundColor: '#fafafa',
    border: '1px solid #f0f0f0',
  },
  infoIcon: {
    width: 36, height: 36, borderRadius: 9,
    backgroundColor: `${ORANGE}18`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: ORANGE, flexShrink: 0,
  },
};

export default Contact;
